import React from "react";
import { ReviewTileItem } from "@/types/tile.types";

const TileField = ({ label, value }: { label: string; value?: React.ReactNode }) => {
  if (!value) return null;
  return (
    <div className="text-sm">
      <p className="text-neutral-400 whitespace-pre-wrap">
        <strong>{label}:&nbsp;</strong>
        {value}
      </p>
    </div>
  );
};

export const useFormatTile = () => {
  const formatTileContent = (tile: ReviewTileItem) => {
    const item = tile.originalData;
    const tileType = tile.originalKey;

    const content: React.ReactNode[] = [];

    if (item.documentName) {
      content.push(<TileField key="docName" label="Document Name" value={item.documentName} />);
    }

    switch (tileType) {
      case "riskPoints":
        content.push(<TileField key="clause" label="Clause" value={item.clause} />);
        content.push(<TileField key="riskText" label="Risk Text" value={item.riskText} />);
        content.push(
          <TileField key="riskExplanation" label="Risk Explanation" value={item.riskExplanation} />
        );
        content.push(<TileField key="suggestions" label="Suggestion" value={item.suggestions} />);
        break;
      case "ambiguities":
        content.push(<TileField key="clause" label="Clause" value={item.clause} />);
        content.push(
          <TileField key="ambiguousText" label="Ambiguous Text" value={item.ambiguousText} />
        );
        content.push(<TileField key="reason" label="Reason" value={item.reason} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      case "conflictingTerms":
        content.push(
          <TileField key="clause1" label="Clause Number" value={item.clause1?.number} />
        );
        content.push(
          <TileField key="clause2" label="Clause Number" value={item.clause2?.number} />
        );
        content.push(
          <TileField
            key="relevant_text"
            label="Relevant Text"
            value={item.clause1?.relevant_text}
          />
        );
        content.push(<TileField key="reason" label="Reason" value={item.reason} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      case "complexLanguage":
        content.push(<TileField key="clause" label="Clause" value={item.clause} />);
        content.push(<TileField key="complexText" label="Complex Text" value={item.complexText} />);
        content.push(<TileField key="reason" label="Reason" value={item.reason} />);
        content.push(<TileField key="suggestions" label="Suggestions" value={item.suggestions} />);
        break;
      case "negotiationPoints":
        if (item.email) {
          content.push(<TileField key="email" label="Email" value={item.email} />);
        } else {
          content.push(<TileField key="clause" label="Clause" value={item.clause} />);
          content.push(
            <TileField
              key="negotiationText"
              label="Negotiation Text"
              value={item.negotiationText}
            />
          );
          content.push(<TileField key="points" label="Points" value={item.points} />);
          content.push(<TileField key="suggestion" label="Suggestion" value={item.suggestion} />);
        }
        break;
      case "nonStandardClauses":
        content.push(<TileField key="clause" label="Clause" value={item.clause} />);
        content.push(
          <TileField key="nonStandardText" label="Non-Standard Text" value={item.nonStandardText} />
        );
        content.push(<TileField key="reason" label="Reason" value={item.reason} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      case "undefinedTerms":
        content.push(<TileField key="contextText" label="Context" value={item.contextText} />);
        content.push(<TileField key="reason" label="Reason" value={item.reason} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      case "placeholders":
        content.push(<TileField key="placeholder" label="Placeholder" value={item.placeholder} />);
        break;
      case "crossReferencingClauses":
        content.push(<TileField key="clause" label="Clause" value={item.clause} />);
        content.push(<TileField key="context" label="Context" value={item.context} />);
        content.push(<TileField key="suggestion" label="Suggestion" value={item.suggestion} />);
        break;
      case "sequentialOrder":
        content.push(
          <TileField key="currentNumber" label="Current Number" value={item.currentNumber} />
        );
        content.push(<TileField key="context" label="Context" value={item.context} />);
        content.push(
          <TileField key="suggestedNumber" label="Suggested Number" value={item.suggestedNumber} />
        );
        content.push(<TileField key="reason" label="Reason" value={item.reason} />);
        break;
      case "missingClauses":
        content.push(<TileField key="clause" label="Clause" value={item.clause} />);
        content.push(<TileField key="reason" label="Reason" value={item.reason} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        content.push(
          <TileField
            key="insertionPoint"
            label="Insertion Point Under"
            value={item.insertionPoint}
          />
        );
        break;
      case "inconsistencies":
        if (item.document1 && item.document2) {
          content.push(<TileField key="doc1" label="Document 1" value={item.document1} />);
          content.push(<TileField key="doc2" label="Document 2" value={item.document2} />);
        }
        content.push(<TileField key="text1" label="Text 1" value={item.text1} />);
        content.push(<TileField key="text2" label="Text 2" value={item.text2} />);
        content.push(<TileField key="explanation" label="Explanation" value={item.explanation} />);
        content.push(<TileField key="suggestion" label="Suggestion" value={item.suggestion} />);
        break;
      case "customReview":
        content.push(<TileField key="clause" label="Clause" value={item.clause} />);
        content.push(
          <TileField key="relevantText" label="Relevant Text" value={item.relevantText} />
        );
        content.push(<TileField key="reason" label="Reason" value={item.reason} />);
        content.push(<TileField key="suggestion" label="Suggestion" value={item.suggestion} />);
        break;
      case "compare_precedent_additional_clauses":
        content.push(<TileField key="info" label="Info" value={item.info} />);
        content.push(
          <TileField key="clauseNumber" label="Clause Number" value={item.clauseNumber} />
        );
        content.push(
          <TileField
            key="additionalClause"
            label="Additional Clause"
            value={item.additionalClause}
          />
        );
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      case "compare_precedent_general_comparison":
        content.push(
          <TileField key="precedentText" label="Precedent Text" value={item.precedentText} />
        );
        content.push(<TileField key="otherText" label="Other Text" value={item.otherText} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      case "compare_precedent_missing_clauses":
        content.push(<TileField key="info" label="Info" value={item.info} />);
        content.push(<TileField key="clause" label="Clause" value={item.clause} />);
        content.push(
          <TileField key="missingClause" label="Missing Clause" value={item.missingClause} />
        );
        content.push(<TileField key="otherText" label="Other Text" value={item.otherText} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      case "compare_precedent_unfavorable_terms":
        content.push(<TileField key="info" label="Info" value={item.info} />);
        content.push(
          <TileField key="precedentText" label="Precedent Text" value={item.precedentText} />
        );
        content.push(<TileField key="otherText" label="Other Text" value={item.otherText} />);
        content.push(<TileField key="analysis" label="Analysis" value={item.analysis} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      case "compare_precedent_legislation_check":
        content.push(
          <TileField
            key="complianceImpact"
            label="Compliance Impact"
            value={item.complianceImpact}
          />
        );
        content.push(
          <TileField key="precedentText" label="Precedent Text" value={item.precedentText} />
        );
        content.push(
          <TileField key="legislationText" label="Legislation Text" value={item.legislationText} />
        );
        content.push(<TileField key="analysis" label="Analysis" value={item.analysis} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      case "compare_precedent_inconsistencies":
        content.push(<TileField key="info" label="Info" value={item.info} />);
        content.push(<TileField key="clause" label="Clause" value={item.clause} />);
        content.push(
          <TileField key="precedentText" label="Precedent Text" value={item.precedentText} />
        );
        content.push(<TileField key="otherText" label="Other Text" value={item.otherText} />);
        content.push(<TileField key="explanation" label="Explanation" value={item.explanation} />);
        content.push(
          <TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />
        );
        break;
      default:
        return (
          <p className="text-sm">
            {item.reason || item.analysis || item.explanation || "No content available"}
          </p>
        );
    }

    return <>{content}</>;
  };

  return { formatTileContent };
};
